<template>
	<div class="google-map-component">
		<div class="google-map" :id="mapName"></div>
	</div>
</template>

<script>
import uniqueid from '@/utils/uniqueid'
import { MAP_MODE_SINGLE, MAP_MODE_MULTI, MAP_MODE_ZONE } from '@/utils/maps'
import GoogleMapsLoader from 'google-maps'
export default {
	props: {
		mode: {
			type: Number,
			default: MAP_MODE_SINGLE
		},
		reading: {
			type: Object,
			default: () => null
		},
		readings: {
			type: Array,
			default: () => []
		},
		zones: {
			type: Array,
			default: () => []
		},
		selected: {
			type: Object,
			default: () => null
		},
		estimated: {
			type: Object,
			default: () => null
		},
		mapOptions: {
			type: Object,
			default: () => {
				return {
					zoom: 16
				}
			}
		}
	},
	data () {
		return {
			//sketchpixelCoords: ['41.5518368', '-8.4242315'],
			map: null,
			marker: null,
			markers: [],
			markersPath: null,
			estimatedMarker: null
		}
	},
	computed: {
		mapName () {
			return `google-map-${uniqueid()}`
		}
	},
	watch: {
		zones (zones, oldZones) {
			// clear previous zones
			oldZones.forEach(zone => {
				zone.polygon.setMap(null)
			})
			this.createZones()
		},
		selected () {
			this.setupMarkers()
		},
		estimated () {
			this.updateEstimatedMarker()
		}
	},
	methods: {
		isMode (mode) {
			return this.mode === mode
		},
		setupMap () {
			// get element and create map
			const element = document.getElementById(this.mapName)
			if (element === null) {
				this.$log.error('google map', 'setupMap() ERROR', 'element is null')()
				return false
			}
			const options = {
				//center: new google.maps.LatLng(...this.sketchpixelCoords),
				center: new google.maps.LatLng(0, 0),
				...this.mapOptions
			}
			this.map = new google.maps.Map(element, options)
			// minimum zoom when bounds are small
			google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
				this.map.setZoom(Math.min(this.mapOptions.zoom, this.map.getZoom()))
			})
			this.setupZones()
			this.setupMarkers()
		},
		setupZones () {
			// create zones
			if (this.zones.length) {
				this.createZones()
			} else {
				//this.$log.error('google map', 'setupMap() ERROR', 'zones is empty')()
			}
		},
		setupMarkers () {
			// create marker
			if (this.isMode(MAP_MODE_SINGLE)) {
				if (this.reading) {
					this.createMarker()
				} else {
					//this.$log.error('google map', 'setupMap() ERROR', 'reading is null')()
				}
			}
			// create markers
			if (this.isMode(MAP_MODE_MULTI)) {
				if (this.readings.length) {
					this.createMarkers()
				} else {
					//this.$log.error('google map', 'setupMap() ERROR', 'readings is empty')()
				}
			}
		},
		createZones () {
			if (this.isMode(MAP_MODE_ZONE)) {
				if (this.zones.length) {
					let zone = this.zones[0]
					if (zone) {
						zone.bounds = new google.maps.LatLngBounds()
						zone.vertices.forEach(vertice => {
							const position = new google.maps.LatLng(vertice.lat, vertice.lng)
							zone.bounds.extend(position)
						})
						zone.polygon.setMap(this.map)
						this.map.fitBounds(zone.bounds)
					}
				}
			} else {
				this.zones.forEach(zone => {
					zone.polygon.setMap(this.map)
				})
			}
		},
		createMarker () {
			// clear previous
			if (this.marker) {
				this.marker.setMap(null)
			}
			// create marker
			const icon = {
				url: '/static/svg/maps/circle-main.svg',
				size: new google.maps.Size(40, 40),
				origin: new google.maps.Point(0, 0),
				anchor: new google.maps.Point(20, 20),
				scaledSize: new google.maps.Size(40, 40)
			}
			const position = new google.maps.LatLng(...this.reading.position)
			this.marker = new google.maps.Marker({
				position: position,
				icon: icon,
				title: `• ${this.$moment(this.reading.date).format('YYYY-MM-DD HH:mm:ss')} • lat: ${this.reading.position[0]} lng: ${this.reading.position[1]}`,
				map: this.map
			})
			const bounds = new google.maps.LatLngBounds()
			bounds.extend(position)
			this.map.fitBounds(bounds)
		},
		createMarkers () {
			// clear previous
			this.markers.forEach(marker => {
				marker.setMap(null)
			})
			if (this.estimatedMarker) {
				this.estimatedMarker.setMap(null)
				this.estimatedMarker = null
			}
			// create path
			const pathCoordinates = []
			this.readings.forEach(element => {
				pathCoordinates.push({ lat: element.values.latitude, lng: element.values.longitude })
			})
			const lineSymbol = {
				path: 'M 0,-1 0,1',
				strokeColor: '#00a2ac',
				strokeOpacity: 1,
				scale: 4,
			}
			this.positionsPath = new google.maps.Polyline({
				path: pathCoordinates,
				geodesic: true,
				strokeOpacity: 0,
				icons: [{
					icon: lineSymbol,
					offset: '0',
					repeat: '20px'
				}],
				map: this.map
			})
			// create markers
			const icon = {
				url: '/static/svg/maps/circle-gray.svg',
				size: new google.maps.Size(10, 10),
				origin: new google.maps.Point(0, 0),
				anchor: new google.maps.Point(5, 5),
				scaledSize: new google.maps.Size(10, 10)
			}
			const iconSelected = {
				url: '/static/svg/maps/circle-yellow.svg',
				size: new google.maps.Size(30, 30),
				origin: new google.maps.Point(0, 0),
				anchor: new google.maps.Point(15, 15),
				scaledSize: new google.maps.Size(30, 30)
			}
			const iconLast = {
				url: '/static/svg/maps/circle-main.svg',
				size: new google.maps.Size(40, 40),
				origin: new google.maps.Point(0, 0),
				anchor: new google.maps.Point(20, 20),
				scaledSize: new google.maps.Size(40, 40)
			}
			const bounds = new google.maps.LatLngBounds()
			const size = this.readings.length
			let i = 0
			this.readings.forEach(element => {
				const position = new google.maps.LatLng(element.values.latitude, element.values.longitude)
				bounds.extend(position)
				i++
				const marker = new google.maps.Marker({
					position: position,
					icon: this.selected && (element.id == this.selected.id) ? iconSelected : (i === size ? iconLast : icon),
					optimized: false,
					zIndex: this.selectedReading && (element.id == this.selectedReading.id) ? 1 : 0,
					title: `• ${this.$moment(element.datetime).format('YYYY-MM-DD HH:mm:ss')} • lat: ${element.values.latitude} lng: ${element.values.longitude}`,
					map: this.map
				})
				this.markers.push(marker)
			})
			this.map.fitBounds(bounds)
		},
		updateEstimatedMarker () {
			// update marker
			if (this.estimated && this.estimatedMarker) {
				this.estimatedMarker.setPosition(new google.maps.LatLng(this.estimated.lat, this.estimated.lng))
				return
			}
			// create new
			if (this.estimated && !this.estimatedMarker) {
				const icon = {
					url: '/static/svg/maps/circle-yellow.svg',
					size: new google.maps.Size(30, 30),
					origin: new google.maps.Point(0, 0),
					anchor: new google.maps.Point(15, 15),
					scaledSize: new google.maps.Size(30, 30)
				}
				const position = new google.maps.LatLng(this.estimated.lat, this.estimated.lng)
				const marker = new google.maps.Marker({
					position: position,
					icon: icon,
					optimized: false,
					zIndex: 1,
					title: 'estimated',
					map: this.map
				})
				this.markers.push(marker)
				this.estimatedMarker = marker
				return
			}
			// clear marker
			if (!this.estimated && this.estimatedMarker) {
				this.estimatedMarker.setMap(null)
				this.estimatedMarker = null
			}
		}
	},
	mounted () {
		GoogleMapsLoader.KEY = this.$root.settings.googleMapsKey
		GoogleMapsLoader.LIBRARIES = this.$root.settings.googleMapsLibraries
		GoogleMapsLoader.load(() => {
			this.setupMap()
		})
	}
}
</script>




// WEBPACK FOOTER //
// src/components/maps/GoogleMap.vue